import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import api from '../utils/api'
import Step1SelectParty from '../components/UpdateInvoice/Step1SelectParty'
import Step2UpdateInvoice from '../components/UpdateInvoice/Step2UpdateInvoice'
import InvoiceSummary from '../components/HourlyInvoice/InvoiceSummary'
import InvoicePreview from '../components/HourlyInvoice/InvoicePreview'
import useAdminFee from '../components/HourlyInvoice/useAdminFee'
import { splitWhole, byClientIdAsc, positionNumber } from '../components/case-detail/parties/partyUtils'

/**
 * /update-invoice/:caseId — port of Scale Portal's update-invoice flow.
 *
 * Steps (Scale parity, widened to N parties):
 *   1. Select Party — one row per billable party on the case, plus an
 *      "All parties" row when more than one is eligible (Step1SelectParty)
 *   2. Update Invoice — client info + line items + processing fee, one
 *      client-info card per SELECTED party (Step2UpdateInvoice)
 *   3. Preview — InvoiceSummary + InvoicePreview + Update Invoice button
 *
 * A fixed case used to have at most two billable parties (primary plaintiff
 * / primary defendant), which is why this used to carry two hardcoded
 * slots. `case_parties` now lets a fixed case add MORE parties per side, so
 * this page works off a PARTY LIST built from
 * `additionalPlaintiffParties`/`additionalDefendantParties` (identity) and
 * `additionalPlaintiffPayments`/`additionalDefendantPayments` (index-aligned
 * payment rows, null where a party has never been invoiced) —
 * casemanager-be's getHourlyBillingDetails now returns all four.
 *
 * Steps 1+2 render inside a Dialog (modal). Step 3 is full-page.
 */

function isPaidStatus(p, includeManual = true) {
    const s = (p?.rawStatus || p?.status || '').toLowerCase()
    return includeManual ? (s === 'paid' || s === 'paid_manually') : s === 'paid'
}

function totalOf(payment) {
    return Number(payment?.grandTotal) || Number(payment?.grand_total) || Number(payment?.amount) || 0
}

// Same gate Step1SelectParty.jsx uses to decide which cards are clickable.
// Duplicated rather than imported (matches this file's existing convention
// of Step-local copies of totalOf/isPaidStatus) because this page needs it
// BEFORE Step1 renders, to know which party keys "All parties" resolves to.
function isSelectableStatus(payment) {
    const s = (payment?.rawStatus || payment?.status || '').toLowerCase()
    return s === 'pending' || s === 'paid_manually' || s === 'held'
}

export default function UpdateInvoice() {
    const { caseId } = useParams()
    const navigate = useNavigate()

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [caseData, setCaseData] = useState(null)
    const [step, setStep] = useState(1)
    const [submitting, setSubmitting] = useState(false)
    const [toast, setToast] = useState(null)
    const [stepError, setStepError] = useState(null)
    const [previewing, setPreviewing] = useState(false)

    // Form state
    // selectedOption is either a party's own key ('plaintiff' | 'defendant' |
    // 'additional_plaintiff:<client_id>' | 'additional_defendant:<client_id>')
    // for a single-party edit, or the sentinel 'all' for "All parties". No
    // party key can ever literally equal 'all', so the two never collide.
    const [selectedOption, setSelectedOption] = useState(null)
    const [items, setItems] = useState([])
    const [includeProcessingFee, setIncludeProcessingFee] = useState(false)
    // Per-party client-info edits, keyed by client_id — not two fixed slots —
    // so any number of parties can carry their own in-flight edits.
    const [clientInfoByClientId, setClientInfoByClientId] = useState({})

    useEffect(() => {
        const t = setTimeout(() => setToast(null), 4000)
        return () => clearTimeout(t)
    }, [toast])

    // Bootstrap
    useEffect(() => {
        let cancelled = false
        ;(async () => {
            try {
                const { data } = await api.get(`/cases/${caseId}/hourly-billing/details`)
                if (cancelled) return
                setCaseData(data?.data || null)
            } catch (err) {
                if (!cancelled) setError(err?.response?.data?.message || 'Failed to load case')
            } finally {
                if (!cancelled) setLoading(false)
            }
        })()
        return () => { cancelled = true }
    }, [caseId])

    // getHourlyBillingDetails returns the richer shape: plaintiffPayments /
    // defendentPayments arrays (note 'defendent' spelling — it is part of the
    // API contract from caseController and must be preserved), the two
    // ADDITIONAL-party identity arrays, their INDEX-ALIGNED payment arrays,
    // plus mediator (admin-fee config) and coupon (already applied on the
    // case).
    const plaintiff = caseData?.plaintiff || null
    const defendant = caseData?.defendant || null
    const mediatorRecord = caseData?.mediator || null
    const bootstrapCoupon = caseData?.coupon || null
    const pid = plaintiff?.client_id ?? plaintiff?.clientId
    const did = defendant?.client_id ?? defendant?.clientId
    // Payment rows come back with RAW snake_case DB columns (.invoice_id,
    // .due_date, .invoice_number) — use those directly below.
    const plaintiffInvoice = useMemo(() => caseData?.plaintiffPayments?.[0] || null, [caseData])
    const defendantInvoice = useMemo(() => caseData?.defendentPayments?.[0] || null, [caseData])
    // Wrapped in useMemo (not a bare `|| []`) so these are referentially
    // stable across renders — a fresh [] every render would make the
    // `allParties` memo below recompute unconditionally, defeating the point.
    const additionalPlaintiffParties = useMemo(() => caseData?.additionalPlaintiffParties || [], [caseData])
    const additionalDefendantParties = useMemo(() => caseData?.additionalDefendantParties || [], [caseData])
    const additionalPlaintiffPayments = useMemo(() => caseData?.additionalPlaintiffPayments || [], [caseData])
    const additionalDefendantPayments = useMemo(() => caseData?.additionalDefendantPayments || [], [caseData])

    // Every party POSITION on the case, in display order (primary plaintiff,
    // additional plaintiffs, primary defendant, additional defendants) —
    // regardless of whether that position has ever been invoiced. `key` is
    // this party's identity everywhere below: it doubles as the `client_type`
    // scale-be stores on the payment row and reads back on a single-party
    // ("selectedOption: 'one'") edit. The primary two keep their EXACT legacy
    // keys ('plaintiff' / 'defendant') so a single-party edit of either one
    // is byte-identical to before this UI existed; an additional party gets
    // `additional_<side>:<client_id>` — scale-be does not validate this
    // string, it only echoes it back to find the one payment being edited.
    const allParties = useMemo(() => {
        const list = []
        if (plaintiff) {
            list.push({
                key: 'plaintiff', side: 'plaintiff', isPrimary: true,
                client_id: pid, label: 'Plaintiff', party: plaintiff, invoice: plaintiffInvoice,
            })
        }
        additionalPlaintiffParties.forEach((p, i) => {
            list.push({
                key: `additional_plaintiff:${p.client_id}`, side: 'plaintiff', isPrimary: false,
                client_id: p.client_id, label: `Plaintiff ${positionNumber(p) ?? i + 2}`,
                party: p, invoice: additionalPlaintiffPayments[i] || null,
            })
        })
        if (defendant) {
            list.push({
                key: 'defendant', side: 'defendant', isPrimary: true,
                client_id: did, label: 'Defendant', party: defendant, invoice: defendantInvoice,
            })
        }
        additionalDefendantParties.forEach((p, i) => {
            list.push({
                key: `additional_defendant:${p.client_id}`, side: 'defendant', isPrimary: false,
                client_id: p.client_id, label: `Defendant ${positionNumber(p) ?? i + 2}`,
                party: p, invoice: additionalDefendantPayments[i] || null,
            })
        })
        return list
    }, [plaintiff, defendant, pid, did, plaintiffInvoice, defendantInvoice, additionalPlaintiffParties, additionalDefendantParties, additionalPlaintiffPayments, additionalDefendantPayments])

    // Billable = has a payment row to edit. A party with none at all cannot
    // be selected — Step1SelectParty shows those in a short note instead.
    const billableParties = useMemo(() => allParties.filter((p) => !!p.invoice), [allParties])
    const unavailableParties = useMemo(() => allParties.filter((p) => !p.invoice), [allParties])
    const partiesByKey = useMemo(
        () => Object.fromEntries(billableParties.map((p) => [p.key, p])),
        [billableParties]
    )

    // Recipient list each party's card was seeded with, keyed by client_id.
    // Save sends invoice_emails only when the case manager changed it from
    // this value. An untouched card sent its seeded value back: before
    // onboarding that was the lead attorney alone (nothing is stored yet), and
    // an editor opened before onboarding and saved after it overwrote the full
    // team onboarding had just saved. Leaving the key out keeps what is stored.
    const seededRecipientsRef = useRef({})
    const sameRecipients = (a, b) => {
        const norm = (v) => String(v ?? '').split(',').map((s) => s.trim()).filter(Boolean).join(',')
        return norm(a) === norm(b)
    }

    const infoFor = (clientId) => clientInfoByClientId[clientId] || {}
    const setInfoFor = (clientId, patchOrFn) => {
        setClientInfoByClientId((prev) => ({
            ...prev,
            [clientId]: typeof patchOrFn === 'function'
                ? patchOrFn(prev[clientId] || {})
                : { ...(prev[clientId] || {}), ...patchOrFn },
        }))
    }

    // Pre-fill client info for EVERY billable party once the case loads —
    // not just the currently-selected one(s) — so switching Step 1's
    // selection never re-fetches or blanks a card that was already seeded.
    // Never overwrites an in-flight edit: only fills entries not yet present.
    useEffect(() => {
        if (!billableParties.length) return
        const buildInfo = (invoice, party) => {
            const rawEmails = invoice?.invoice_emails ?? invoice?.invoiceEmails
            return {
                name: invoice?.invoice_customer_name || invoice?.invoiceCustomerName || party?.name || '',
                email: Array.isArray(rawEmails)
                    ? rawEmails[0] || party?.email || ''
                    : (party?.email || ''),
                phone: party?.phone || party?.phone_number || '',
                address: invoice?.invoice_address || invoice?.invoiceAddress || '',
                invoice_emails: Array.isArray(rawEmails)
                    ? rawEmails.join(',')
                    : (typeof rawEmails === 'string' ? rawEmails : (party?.email || '')),
            }
        }
        billableParties.forEach((p) => {
            if (!(p.client_id in seededRecipientsRef.current)) {
                seededRecipientsRef.current[p.client_id] = buildInfo(p.invoice, p.party).invoice_emails
            }
        })
        setClientInfoByClientId((prev) => {
            const next = { ...prev }
            let changed = false
            billableParties.forEach((p) => {
                if (next[p.client_id]) return
                next[p.client_id] = buildInfo(p.invoice, p.party)
                changed = true
            })
            return changed ? next : prev
        })
    // eslint-disable-next-line
    }, [billableParties])

    // Pre-fill LINE ITEMS from the invoice(s) being edited whenever the
    // selection changes. For a single party this is that party's own items;
    // for "All parties" this is the first selectable party's items — Step 1
    // already guarantees every selectable party's items are identical before
    // "All parties" can be chosen, so any one of them is representative.
    useEffect(() => {
        if (!selectedOption || !billableParties.length) return
        const sourceInvoice = selectedOption === 'all'
            ? billableParties.find((p) => isSelectableStatus(p.invoice))?.invoice
            : partiesByKey[selectedOption]?.invoice
        let sourceItems = sourceInvoice?.items
        if (typeof sourceItems === 'string') {
            try { sourceItems = JSON.parse(sourceItems) } catch (_e) { sourceItems = [] }
        }
        if (Array.isArray(sourceItems) && sourceItems.length > 0) {
            setItems(sourceItems.map((it, i) => ({
                id: it.id || (i + 1),
                description: it.description || '',
                amount: Number(it.amount) || (Number(it.quantity) || 1) * (Number(it.rate) || Number(it.unit_price) || 0),
            })))
        } else if (sourceInvoice?.amount) {
            setItems([{ id: 1, description: 'Mediation Session', amount: Number(sourceInvoice.amount) }])
        } else {
            setItems([{ id: 1, description: '', amount: 0 }])
        }
        setIncludeProcessingFee(!!(sourceInvoice?.include_processing_fee ?? sourceInvoice?.includeProcessingFee))
    // eslint-disable-next-line
    }, [selectedOption, billableParties])

    // Mirrors the mediator portal's DISABLE_FIXED_UPDATE_PROCESSING_FEE
    // (FixedUpdateInvoice/fixedUpdateInvoiceUiFlags.js). Set false to restore the
    // legacy toggle.
    //
    // Keeping the fee off the edit UI is the easy half. The half that matters is
    // that the payload must NOT carry an explicit `false`, because
    // payments.include_processing_fee also decides WHICH LINK the invoice email
    // carries: false routes the client to the plain Stripe invoice page instead
    // of the payment gateway, so they never get the ACH/card choice and the
    // mediator ends up absorbing the card fee. Sending null leaves the stored
    // per-party value alone, so a booking-written `true` survives an edit.
    // This portal previously sent `!!includeProcessingFee` — always an explicit
    // boolean, and one shared across BOTH parties on a "both" edit, so editing
    // either side could clobber the other's stored value.
    const DISABLE_FIXED_UPDATE_PROCESSING_FEE = true

    const subtotal = useMemo(() => (items || []).reduce((s, it) => s + (Number(it.amount) || 0), 0), [items])

    const isAllSelected = selectedOption === 'all'

    // Every party the current selection targets, in split order.
    // byClientIdAsc (same helper the Modify-Parties flow uses) keeps the +1c
    // remainder on a deterministic party (lowest client_id) rather than
    // always "whichever came first in the object literal", so the same case
    // always rounds the same way regardless of how many parties are on it.
    // partyCount is driven by the LENGTH of this list — 2 today, N once a
    // case actually carries additional parties, never hardcoded.
    const splitPartyKeys = useMemo(() => {
        if (isAllSelected) {
            return billableParties
                .filter((p) => isSelectableStatus(p.invoice))
                .sort(byClientIdAsc)
                .map((p) => p.key)
        }
        return selectedOption ? [selectedOption] : []
    }, [isAllSelected, selectedOption, billableParties])
    const partyCount = splitPartyKeys.length || 1
    // isBoth kept as an alias — the string 'both' is also what gets sent to
    // scale-be as `selectedOption` whenever more than one party is targeted
    // (see handleUpdate), so this is genuinely "isMulti", not "is exactly 2".
    const isBoth = splitPartyKeys.length > 1

    // Each line item is split SUM-PRESERVING across the selected parties
    // (2000/2 -> 1000/1000; 2000/3 -> 667/667/666) using the same splitWhole
    // helper the Modify-Parties flow uses — no new rounding logic. This must
    // feed BOTH the payload sent to scale-be AND the preview: scale-be
    // recomputes each party's subtotal by SUMMING that party's own `items`
    // array and ignores `amount` when items are present
    // (fixedInvoiceUpdatePayloadBuilder.helper.js: calculateAmountAndItems), so
    // the split has to live in `items`, not just in a halved top-level
    // `amount` — sending full items with a halved `amount` is exactly what let
    // the previous attempt bill each party in full while the preview promised
    // half.
    //
    // For a single-party edit (partyCount === 1) splitWhole(amount, 1) returns
    // the amount unchanged, so this reduces to today's behavior with no
    // rounding drift.
    const splitItemsByKey = useMemo(() => {
        const map = {}
        splitPartyKeys.forEach((k) => { map[k] = [] })
        ;(items || []).forEach((it) => {
            const shares = splitWhole(Number(it.amount) || 0, partyCount)
            splitPartyKeys.forEach((k, i) => {
                map[k].push({ id: it.id, description: it.description, amount: shares[i] })
            })
        })
        return map
    }, [items, splitPartyKeys, partyCount])

    const partyShareOf = (key) =>
        (splitItemsByKey[key] || []).reduce((s, it) => s + (Number(it.amount) || 0), 0)

    // Keyed by client_id (not clientType) because InvoicePreview identifies the
    // currently-toggled party by client_id.
    const perPartySharesByClientId = isBoth
        ? Object.fromEntries(
              splitPartyKeys.map((k) => [partiesByKey[k]?.client_id, partyShareOf(k)])
          )
        : null
    const perPartyPercentage = partyCount > 0 ? Math.round((100 / partyCount) * 10) / 10 : 100

    const effectiveIncludeProcessingFee =
        DISABLE_FIXED_UPDATE_PROCESSING_FEE ? false : includeProcessingFee
    // null = "leave the stored value alone", matching the mediator portal.
    const payloadIncludeProcessingFee =
        DISABLE_FIXED_UPDATE_PROCESSING_FEE ? null : !!includeProcessingFee
    const processingFee = effectiveIncludeProcessingFee
        ? Math.floor((subtotal * 0.029 + 0.3) * 100) / 100
        : 0
    const grandTotal = subtotal + processingFee

    // Admin fee already collected when the source invoice is paid/paid_manually.
    // Computed at top level so it can feed useAdminFee (must run before any
    // early return — hook rules).
    //
    // On a "both" edit this must be AND, not OR. The mediator portal is explicit
    // about it (FixedUpdateInvoice/common/InvoiceSummary.jsx:210-213 — "If both
    // selected, admin fee is collected only if BOTH are paid").
    //
    // With OR, one already-paid party suppressed the admin fee for the OTHER,
    // still-unpaid one: `totalAdminFee` went to 0 in useAdminFee, so the
    // "Scale Mediation Admin Fee" row vanished from the preview and its amount
    // read as nothing. A party who has not paid still owes the fee.
    //
    // Scope note, because it is easy to assume otherwise: this is a PREVIEW fix
    // only. It does NOT change the invoice the client downloads. scale-be
    // derives `shouldAddAdminFeeAsLineItem = isClientPayAdminFee &&
    // !isCouponApplied` server-side (payment.js:1106) and deliberately ignores
    // the `is_admin_fee_added_in_line_item` we send — its own comment says the
    // server-side derivation "ensures admin fee appears in Stripe PDF even if
    // frontend didn't send is_admin_fee_added_in_line_item".
    const adminFeeCollected = splitPartyKeys.length > 0
        && splitPartyKeys.every((k) => isPaidStatus(partiesByKey[k]?.invoice))

    // Admin fee + processing fee, mirroring Scale's useAdminFee. 'all' for a
    // multi-party update (split across parties), 'one' for a single party.
    const adminFeeData = useAdminFee({
        subtotal,
        selectedOption: isBoth ? 'all' : 'one',
        clients: billableParties.map((p) => ({ client_id: p.client_id, ...p.party })),
        mediatorRecord,
        isAdminFeeCollected: adminFeeCollected,
        isCouponApplied: !!bootstrapCoupon,
        mediatorProcessingFeeToClient: !!mediatorRecord?.mediationRates?.processing_fee_to_client,
        // This IS the fixed-invoice editor: the shared hook otherwise applies the
        // hourly flow's `/ 2` to the percentage base and divides the fee across
        // parties, both of which are wrong here. The mediator portal sets the
        // same flag for its whole fixed flow.
        isFixedPriceInvoice: true,
    })

    const handleStep1Continue = () => {
        if (!selectedOption) {
            setToast({ type: 'error', message: 'Please select a party to update.' })
            return
        }
        setStep(2)
    }

    const handleStep2Continue = async () => {
        if (previewing) return // double-click guard
        setPreviewing(true)
        // Force-blur the active input so any pending HoursInput / amount
        // input commits its value to React state before we read it.
        if (document.activeElement && typeof document.activeElement.blur === 'function') {
            document.activeElement.blur()
        }
        await new Promise((r) => setTimeout(r, 30))

        try {
            const latestItems = items || []
            const latestSubtotal = latestItems.reduce((acc, it) => acc + (Number(it.amount) || 0), 0)
            if (!latestItems.length || latestSubtotal <= 0) {
                setStepError('Please add at least one line item with a valid amount')
                return
            }
            if (latestItems.some((it) => !it.description || Number(it.amount) <= 0)) {
                setStepError('Please ensure all line items have a description and valid amount')
                return
            }
            setStepError(null)
            setStep(3)
        } finally {
            setPreviewing(false)
        }
    }

    const handleUpdate = async () => {
        try {
            setSubmitting(true)
            // Build per-party payments array (Scale-shape). Payment rows are
            // raw snake_case from getHourlyBillingDetails (.id, .invoice_id,
            // .due_date, .status).
            //
            // "Both" SPLITS the entered total across the selected parties —
            // Step 2 already tells the case manager this is what happens, and
            // scale-be recomputes each party's subtotal by SUMMING that
            // party's own `items` array (ignoring `amount` whenever items are
            // present), so the split share has to be baked into `items`
            // itself. `splitItemsByKey` / `partyShareOf` above do exactly that
            // — see their comments for why a halved top-level `amount` alone
            // is not enough (that is what let the previous attempt bill each
            // party in full while the preview promised half).
            //
            // A single-party edit is untouched: splitPartyKeys has exactly one
            // key, splitWhole(amount, 1) returns the amount unchanged, so
            // `items`/`amount` here are byte-identical to the pre-split code
            // whenever that one key is the legacy 'plaintiff' or 'defendant'.
            //
            // `client_type` on EACH payment must equal that party's own key —
            // it is how scale-be's selectedOption:'one' path finds which one
            // payment (out of the whole `payments` array it always receives)
            // to actually update: `payments.find(p => p.client_type ===
            // client_type)` against the top-level `client_type` sent below.
            const buildPayment = (key) => {
                const p = partiesByKey[key]
                if (!p) return null
                const info = infoFor(p.client_id)
                const invoice = p.invoice
                const partyItems = splitItemsByKey[key] || []
                const partyAmount = partyShareOf(key)
                const seeded = seededRecipientsRef.current[p.client_id]
                const recipientsUntouched = seeded !== undefined && sameRecipients(info.invoice_emails, seeded)
                return {
                    payment_id: invoice?.id || null,
                    client_id: p.client_id,
                    invoice_id: invoice?.invoice_id || invoice?.invoiceId || null,
                    amount: partyAmount,
                    items: partyItems.map((it) => ({
                        id: it.id,
                        description: it.description,
                        quantity: 1,
                        rate: Number(it.amount) || 0,
                        amount: Number(it.amount) || 0,
                    })),
                    include_processing_fee: payloadIncludeProcessingFee,
                    due_date: invoice?.due_date || invoice?.dueDate || null,
                    invoice_customer_name: info.name || null,
                    invoice_address: info.address || null,
                    invoice_emails: recipientsUntouched ? undefined : (info.invoice_emails || null),
                    client_type: key,
                    is_admin_fee_collected: (invoice?.status || invoice?.rawStatus) === 'paid' || (invoice?.status || invoice?.rawStatus) === 'paid_manually',
                }
            }

            const paymentsBody = splitPartyKeys.map(buildPayment).filter(Boolean)

            // is_zero_amount_coupon: only true when the applied coupon zeroes out
            // the relevant slot amount (half-day for morning/afternoon, full-day
            // for fullday). Default false when no coupon.
            const slotType = String(caseData?.case?.slot_type || '').toLowerCase()
            const isZeroAmountCoupon = bootstrapCoupon
                ? (slotType === 'fullday'
                    ? Number(bootstrapCoupon.full_day_amount) === 0
                    : Number(bootstrapCoupon.half_day_amount) === 0)
                : false

            const res = await api.put(`/billing/update-fixed-proxy`, {
                case_id: Number(caseId),
                selectedOption: isBoth ? 'both' : 'one',
                client_type: isBoth ? undefined : selectedOption,
                payments: paymentsBody,

                // Coupon (already applied on the case, sourced from bootstrap).
                coupon: bootstrapCoupon || null,
                coupon_id: bootstrapCoupon?.id || null,
                is_coupon_applied: !!bootstrapCoupon,
                is_zero_amount_coupon: isZeroAmountCoupon,

                // Admin fee. For 'both', send null so scale-be re-derives the
                // fee PER PARTY from mediator_settings (passed below) — a single
                // pre-computed flat fee would be applied to each party and double-
                // charge. Matches Scale's FE, which never sends a pre-calc fee for
                // 'both'. For one-party, send the computed value (?? not || so a
                // legitimate 0 is sent as 0, not null).
                admin_fee: isBoth ? null : (adminFeeData.adminFeeAmountNumeric ?? null),
                is_admin_fee_added_in_line_item: adminFeeData.is_admin_fee_added_in_line_item || false,

                // Mediator settings passthrough (scale-be reads these).
                mediator_settings: {
                    processing_fee_to_client: mediatorRecord?.mediationRates?.processing_fee_to_client || false,
                    admin_fee_type: mediatorRecord?.adminFee?.admin_fee_type || null,
                    admin_fee_amount: mediatorRecord?.adminFee?.admin_fee_amount || null,
                    admin_fee_pay_by: mediatorRecord?.adminFee?.admin_fee_pay_by || null,
                    custom_percentage_for_coupon: mediatorRecord?.adminFee?.custom_percentage_for_coupon || null,
                },
                include_processing_fee: payloadIncludeProcessingFee,
            })

            // scale-be answers 200 EVEN WHEN AN INVOICE WAS REJECTED. Each payment
            // is updated inside its own try/catch and the outcome is pushed into
            // `results[]`, so the HTTP status only says "the request was handled",
            // not "the invoice changed". Reading it as success reported
            // "Invoice has been updated successfully." for rejections like
            // "Cannot update a paid invoice. Invoice status: paid" — the exact
            // failure the user had asked about. The per-item status is the real
            // signal, so check it.
            const payload = res?.data?.data ?? res?.data ?? {}
            const results = Array.isArray(payload?.results) ? payload.results : []
            const failed = results.filter(
                (r) => r?.status === 'error' || r?.success === false
            )

            if (failed.length) {
                const reason =
                    failed[0]?.message || failed[0]?.reason || 'The invoice could not be updated.'
                setToast({
                    type: 'error',
                    message:
                        failed.length === results.length
                            ? reason
                            : `${results.length - failed.length} of ${results.length} invoices updated. ${reason}`,
                })
                return
            }
            // An explicit success:false with no per-item detail still means it
            // did not happen.
            if (payload?.success === false) {
                setToast({ type: 'error', message: payload?.message || 'Failed to update invoice.' })
                return
            }

            setToast({ type: 'success', message: 'Invoice has been updated successfully.' })
            setTimeout(() => navigate(`/cases/${caseId}`, { state: { refresh: true } }), 1500)
        } catch (err) {
            setToast({ type: 'error', message: err?.response?.data?.message || 'Failed to update invoice. Please try again.' })
        } finally {
            setSubmitting(false)
        }
    }

    // While loading or on error: render the modal shell (blurred backdrop +
    // header + step row + inline loader) so the page doesn't flash empty.
    // Mirrors the Create Invoice flow.
    if (loading || !caseData || error) {
        return (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-xl">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-[1180px] max-h-[92vh] overflow-hidden relative">
                    <button type="button" onClick={() => navigate(`/cases/${caseId}`)} className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 z-50" aria-label="Close">
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 5l10 10M15 5L5 15"/></svg>
                    </button>
                    <div className="px-6 pt-5 pb-3">
                        <h2 className="text-lg font-semibold text-gray-900">Update Invoice</h2>
                        <p className="text-sm text-gray-500 mt-0.5">Choose which invoice to update.</p>
                        <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-1.5">
                                {[1, 2, 3].map((n) => (
                                    <div key={n} className="h-2 w-8 rounded-full" style={n === 1 ? { backgroundColor: '#115E59' } : { backgroundColor: '#E5E7EB' }} />
                                ))}
                            </div>
                            <span className="text-sm text-gray-600 whitespace-nowrap">Step 1 of 3</span>
                        </div>
                    </div>
                    <div className="px-6 py-16 flex items-center justify-center">
                        {error ? (
                            <p className="text-sm text-red-600">{error}</p>
                        ) : (
                            <div className="flex items-center gap-3 text-sm text-gray-500">
                                <span className="inline-block w-4 h-4 rounded-full border-2 border-gray-200 border-t-[#115E59] animate-spin" />
                                Loading…
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    // Build values shape for InvoiceSummary / InvoicePreview reuse.
    // Preserve existing invoice_number from the source invoice; preview should
    // show the existing number rather than a placeholder dash (Scale parity).
    // For "All parties" this is the first party in split order — Step 1
    // already guarantees identical due dates/numbers aren't required to
    // match (only items + grand total are), so this is just a display pick.
    const sourceInv = isBoth
        ? partiesByKey[splitPartyKeys[0]]?.invoice
        : partiesByKey[selectedOption]?.invoice
    const previewValues = {
        invoice_number: sourceInv?.invoice_number || sourceInv?.invoiceNumber || '',
        date: new Date().toISOString().slice(0, 10),
        due_date: sourceInv?.due_date || sourceInv?.dueDate || '',
        subtotal,
        items: items.map((it) => ({ id: it.id, description: it.description, quantity: 1, rate: Number(it.amount), amount: Number(it.amount) })),
        // Pass the REAL selection through. A 'both' update shows the
        // Plaintiff/Defendant switcher in the preview so each side's invoice
        // can be read before submitting — the mediator portal does the same
        // (InvoicePreview.jsx: shouldShowTabs = isEditMode && selectedOption
        // === 'both'), and it is the source of truth here.
        //
        // This was previously forced to 'one', which hid the switcher: the
        // preview only ever rendered the plaintiff and the defendant's invoice
        // could not be seen at all. The per-party figures were already correct
        // (splitFactor / perPartyShare / perPartyPercentage below are computed
        // from the real selectedOption), so only the switcher was missing.
        selectedOption: isBoth ? 'both' : 'one',
        selectedParty: partiesByKey[selectedOption]?.client_id ?? pid,
        additional_notes: '',
        // Admin fee + processing fee so InvoicePreview renders those lines.
        admin_fee: adminFeeData.adminFeeAmountNumeric,
        is_admin_fee_added_in_line_item: adminFeeData.is_admin_fee_added_in_line_item,
        includeProcessingFee: !!includeProcessingFee,
    }
    // hasOwnProperty override (Scale parity): InvoicePreview keys its
    // conditional admin-fee / processing-fee rendering off hasOwnProperty
    // checks; force these two to always report present.
    previewValues.hasOwnProperty = (key) => {
        if (key === 'is_admin_fee_added_in_line_item' || key === 'includeProcessingFee') return true
        return Object.prototype.hasOwnProperty.call(previewValues, key)
    }

    // Flat, display-ready shape for a party: case identity merged with any
    // in-flight Step 2 edits — the same merge InvoiceSummary/InvoicePreview
    // always got for the primary two, generalized to any party.
    const partyDisplay = (p) => {
        if (!p) return null
        const info = infoFor(p.client_id)
        return {
            ...p.party,
            client_id: p.client_id,
            role: p.side,
            name: info.name || p.party?.name || '',
            email: info.email || p.party?.email || '',
            phone: info.phone || p.party?.phone || p.party?.phone_number || '',
            address: info.address || p.party?.address || '',
            invoice_emails: info.invoice_emails || '',
        }
    }

    const selectedKeysSet = new Set(splitPartyKeys)
    const selectedPrimaryPlaintiff = selectedKeysSet.has('plaintiff') ? partiesByKey['plaintiff'] : null
    const selectedPrimaryDefendant = selectedKeysSet.has('defendant') ? partiesByKey['defendant'] : null
    const selectedAdditionalPlaintiffs = billableParties.filter(
        (p) => p.side === 'plaintiff' && !p.isPrimary && selectedKeysSet.has(p.key)
    )
    const selectedAdditionalDefendants = billableParties.filter(
        (p) => p.side === 'defendant' && !p.isPrimary && selectedKeysSet.has(p.key)
    )

    const stepRow = (
        <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-1.5">
                {[1, 2, 3].map((n) => (
                    <div
                        key={n}
                        className="h-2 w-8 rounded-full"
                        style={step === n ? { backgroundColor: '#115E59' } : { backgroundColor: '#E5E7EB' }}
                    />
                ))}
            </div>
            <span className="text-sm text-gray-600 whitespace-nowrap">Step {step} of 3</span>
        </div>
    )

    return (
        <>
            {/* Steps 1 + 2 — modal popup. Same blur + size + z-index as Create Invoice. */}
            {step !== 3 && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-xl">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-[1180px] max-h-[92vh] overflow-hidden relative">
                        <button type="button" onClick={() => navigate(`/cases/${caseId}`)} className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 z-50" aria-label="Close">
                            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 5l10 10M15 5L5 15"/></svg>
                        </button>

                        <div className="px-6 pt-5 pb-3">
                            <h2 className="text-lg font-semibold text-gray-900">{step === 1 ? 'Select Party' : 'Update Invoice'}</h2>
                            <p className="text-sm text-gray-500 mt-0.5">
                                {step === 1 ? 'Choose which invoice to update.' : 'Add invoice details and line items.'}
                            </p>
                            {stepRow}
                        </div>

                        <div className="px-6 py-3 max-h-[60vh] overflow-y-auto">
                            {step === 1 && (
                                <Step1SelectParty
                                    parties={billableParties}
                                    unavailableParties={unavailableParties}
                                    selectedOption={selectedOption}
                                    setSelectedOption={setSelectedOption}
                                    onToast={setToast}
                                />
                            )}
                            {step === 2 && (
                                <Step2UpdateInvoice
                                    selectedParties={splitPartyKeys.map((k) => partiesByKey[k]).filter(Boolean)}
                                    infoByClientId={clientInfoByClientId}
                                    setInfoFor={setInfoFor}
                                    items={items}
                                    setItems={setItems}
                                    includeProcessingFee={effectiveIncludeProcessingFee}
                                    disableProcessingFee={DISABLE_FIXED_UPDATE_PROCESSING_FEE}
                                    setIncludeProcessingFee={setIncludeProcessingFee}
                                    currentAmount={totalOf(sourceInv)}
                                    error={stepError}
                                    setError={setStepError}
                                />
                            )}
                        </div>

                        <div className="px-6 pb-5 flex items-center justify-between">
                            {step === 2 ? (
                                <button type="button" onClick={() => setStep(1)} className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1">
                                    ← Back
                                </button>
                            ) : <span />}
                            {step === 1 && (
                                <button
                                    type="button"
                                    disabled={!selectedOption}
                                    onClick={handleStep1Continue}
                                    className="ml-auto px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50"
                                    style={{ backgroundColor: '#115E59' }}
                                >
                                    Continue
                                </button>
                            )}
                            {step === 2 && (
                                <button
                                    type="button"
                                    disabled={previewing}
                                    onClick={handleStep2Continue}
                                    className="px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-60 inline-flex items-center gap-2"
                                    style={{ backgroundColor: '#115E59' }}
                                >
                                    {previewing && (
                                        <span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                                    )}
                                    {previewing ? 'Loading…' : 'Continue to Preview'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Step 3 — full-screen takeover, sits above the case-detail
                backdrop so nothing bleeds through. */}
            {step === 3 && (
                <div className="fixed inset-0 z-[60] overflow-auto bg-gray-50">
                <div className="p-6 max-w-[1600px] mx-auto space-y-4">
                    <button type="button" onClick={() => navigate(`/cases/${caseId}`)} className="text-sm text-[#115E59] mb-4 hover:underline">← Back to Case</button>
                    {/* Wider page (1280 → 1600) keeps both columns roomy: the
                        summary keeps its 4-column footprint (so the plaintiff
                        / defendant email lines no longer wrap onto a tight
                        single row), and the preview still picks up extra
                        width from the bigger page wrapper. */}
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-4 bg-gray-100 rounded">
                            <InvoiceSummary
                                values={previewValues}
                                onEditRecipient={() => setStep(1)}
                                onEditDetails={() => setStep(2)}
                                plaintiff={partyDisplay(selectedPrimaryPlaintiff)}
                                defendant={partyDisplay(selectedPrimaryDefendant)}
                                additionalPlaintiffParties={selectedAdditionalPlaintiffs.map(partyDisplay)}
                                additionalDefendantParties={selectedAdditionalDefendants.map(partyDisplay)}
                                selectedRecipient={splitPartyKeys.length === 1 ? partyDisplay(partiesByKey[splitPartyKeys[0]]) : null}
                                isAll={isBoth}
                                adminFeeAmount={adminFeeData.adminFeeAmountNumeric}
                                adminFeeCollected={adminFeeCollected}
                                isAdminFeeAddedInLineItem={adminFeeData.is_admin_fee_added_in_line_item}
                            />
                        </div>
                        <div className="col-span-8 bg-gray-100 rounded">
                            <InvoicePreview
                                values={previewValues}
                                mediator={caseData?.mediator}
                                caseData={caseData?.case || caseData}
                                plaintiffOptions={billableParties
                                    .filter((p) => p.side === 'plaintiff' && selectedKeysSet.has(p.key))
                                    .map((p) => ({ value: p.client_id, label: partyDisplay(p).name || p.label }))}
                                defendantOptions={billableParties
                                    .filter((p) => p.side === 'defendant' && selectedKeysSet.has(p.key))
                                    .map((p) => ({ value: p.client_id, label: partyDisplay(p).name || p.label }))}
                                initialClient={partyDisplay(partiesByKey[splitPartyKeys[0]])}
                                selectClientById={(id) => {
                                    const p = billableParties.find((x) => x.client_id === id)
                                    return p ? partyDisplay(p) : null
                                }}
                                splitFactor={partyCount}
                                perPartyShare={isBoth ? subtotal / partyCount : subtotal}
                                perPartySharesByClientId={perPartySharesByClientId}
                                perPartyPercentage={perPartyPercentage}
                                processingFeeToClient={effectiveIncludeProcessingFee}
                                adminFee={adminFeeData.adminFeeAmountNumeric}
                                adminFeePerParty={adminFeeData.adminFeePerParty}
                                isAdminFeeAddedInLineItem={adminFeeData.is_admin_fee_added_in_line_item}
                            />
                        </div>

                        <div className="col-span-12 flex justify-between bg-white border p-4 rounded">
                            <button type="button" onClick={() => setStep(2)} className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1">
                                ← Back to edit
                            </button>
                            <button
                                type="button"
                                disabled={submitting}
                                onClick={handleUpdate}
                                className="px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50"
                                style={{ backgroundColor: '#115E59' }}
                            >
                                {submitting ? 'Updating…' : 'Update Invoice'}
                            </button>
                        </div>
                    </div>
                </div>
                </div>
            )}

            {toast && (
                <div className={`fixed bottom-6 right-6 px-4 py-2 rounded-md shadow-lg text-sm z-50 ${toast.type === 'success' ? 'bg-green-600 text-white' : toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-gray-800 text-white'}`}>
                    {toast.message}
                </div>
            )}
        </>
    )
}
